import React, { useEffect, useMemo, useState } from 'react';
import { debounce, throttle } from 'lodash-es';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { serverSideTranslation } from '../util/next';
import { getPageContent } from '../api/pagesApi';

import NavAnchor, { AnchorProvider } from '../components/NavAnchor';
import Page from '../components/Layout/Page';
import ScrollPosition, { IScrollTopContext } from '../components/ScrollPosition';
import { BreakPoint, useScreenSize } from '../components/ScreenSize';
import { genShareConfig } from '../components/Share/use-config-share';

import WinCardList from '../modules/WinCardList';
import VideoTopBanner from '../modules/VideoTopBanner';
import { Image } from '../components/Image';

import styles from '../modules/WinBanner/winT.module.scss';

interface Props {
  pageContentData: any;
}

function Cooperation(props: Props) {
  // const offset: [number, number] = [0, 10];
  const [isStick, setIsStick] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const { pageContentData } = props;
  const { pageContent = {}, shareContent = {} } = pageContentData || {};
  const { winBannerTitle, winQrImg, winBannerImg, winQrText, winSectionList, h5WinBannerImg, winDetailText } =
    pageContent;

  const sectionList = winSectionList;

  const { screen, size } = useScreenSize();
  const isMobile = screen === BreakPoint.md || screen === BreakPoint.sm;
  // const isMobile = screen === BreakPoint.sm;

  const { locale } = useRouter();

  const handleStickScroll = (position: IScrollTopContext) => {
    const { scrollTop: top } = position;
    /**
     * [BreakPoint.lg, BreakPoint.xl] -> 400 - 60
     * [BreakPoint.xl, Infinity] -> 400 - 80
     * [0, BreakPoint.lg] -> 160 - 60
     * offset: 10
     */
    switch (screen) {
      case BreakPoint.lg:
        setIsStick(top >= 340);
        break;
      case BreakPoint.xl:
      case BreakPoint['2xl']:
        setIsStick(top >= 320);
        break;
      default:
        setIsStick(top >= 160);
    }
  };

  const handleNavTop = useMemo(
    () =>
      throttle(
        (position: IScrollTopContext) => {
          const { scrollTop: top } = position;
          setScrollTop(top);
        },
        isMobile ? 20 : 30,
      ),
    [isMobile],
  );

  const handleScroll = (position: IScrollTopContext) => {
    handleStickScroll(position);
    handleNavTop(position);
  };
  useEffect(() => {
    if (isMobile && size?.width !== 0) {
      window.location.href = 'https://123.meituan.com/v3/biz-coop/win-together?source=mtgw';
    }
    const resizeHandler = debounce(() => {
      resizeHandler();
      // if (isMobile) {
      //   window.open('https://123.meituan.com/v3', '_self');
      // }
    });

    resizeHandler();

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [isMobile, size.width]);

  const videoCoverImg = useMemo(() => {
    if (screen === BreakPoint.xl || screen === BreakPoint['2xl']) {
      return winBannerImg;
    }
    return h5WinBannerImg;
  }, [screen, h5WinBannerImg, winBannerImg]);

  if (isMobile) {
    return <div className="back absolute bottom-0 left-0 right-0 top-0 z-[10004] bg-white" />;
  }

  return (
    <AnchorProvider defaultAnchor={sectionList[0].anchor || ''} scrollTop={scrollTop}>
      <ScrollPosition onScroll={handleScroll}>
        <Page title={shareContent.shareTitle}>
          {!isMobile && (
            <div className="bg-[#F7F8F9]">
              <VideoTopBanner videoTitle={winBannerTitle} videoCoverImg={videoCoverImg} />

              <div className="mx-5 flex h-full max-w-screen-xl justify-between pt-[68px] text-black-dark  l:m-auto">
                <div
                  className={classNames('relative w-full lg:mr-[20px] lg:w-[180px]', {
                    'h-[42px]': isStick && isMobile,
                  })}
                >
                  <div
                    className={classNames({
                      'fixed sticky lg:top-[110px] xl:top-[130px]': isStick && !isMobile,
                      'fixed top-[60px] z-10 w-full bg-[#fff]': isStick && isMobile,
                    })}
                  >
                    <NavAnchor content={sectionList} />
                    <div className="mt-[30px] border-t border-[#edeeef] pb-[20px] pt-[32px]">
                      <div className={styles['banner-qr-box']}>
                        <div className={styles['qr-img-box']}>
                          <Image src={winQrImg} layout="fill" objectFit="cover" />
                        </div>
                        <div className={styles['qr-img-text']}>{winQrText}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 px-5 pb-[100px] lg:mt-[-68px] lg:px-0">
                  {(sectionList || []).map((section: any) => {
                    const { anchor } = section;
                    return <WinCardList key={`${anchor}_${locale}`} {...section} winDetailText={winDetailText} />;
                  })}
                </div>
              </div>
            </div>
          )}
        </Page>
      </ScrollPosition>
    </AnchorProvider>
  );
}

export async function getServerSideProps({ locale, query }: any) {
  const pageContentParams = {
    uri: '/win-together',
    pageContentNo: query.pageContentNo,
    preview: query.preview,
  };
  const pageContentData = await getPageContent(pageContentParams, locale);

  return {
    props: {
      lxCustom: {
        cid: 'c_donation_yarsy0no',
        locale,
        ...pageContentData?.shareContent,
      },
      pageContentData,
      ...genShareConfig(pageContentData),
      ...(await serverSideTranslation(locale, ['cookie', 'common'])),
    },
  };
}

export default Cooperation;
